import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { UsuarioService } from '../../../core/services/usuario.service';
import { EmpresaService } from '../../../core/services/empresa.service';
import { ApiService } from '../../../core/services/api.service';
import { EmpresasDataModel } from '../../../core/models/empresa.data.model';
import {
  UsuarioFormValue,
  UsuariosDataModel,
  UsuariosInfoModel,
} from '../../../core/models/usuario.data.model';
import { PageHeaderComponent } from '../../../shared/page-header/page-header';
import { SummaryCardComponent } from '../../../shared/summary-card/summary-card';
import { DataTablePaginado } from '../../../shared/data-table-paginado/data-table-paginado';
import {
  PaginationRequest,
  TableActionEvent,
  TableColumn,
  TablePageEvent,
  TableSortEvent,
} from '../../../shared/data-table-paginado/data-table-paginado.models';
import { ModalShellComponent } from '../../../shared/modal-shell/modal-shell';
import { UsuariosFormComponent } from './usuarios-form/usuarios-form';
import {
  ConfirmationModalComponent,
  ConfirmationStatus,
} from '../../../shared/confirmation-modal/confirmation-modal';
import { RolesService } from '../../../core/services/roles.service';
import { RolesDto } from '../../../core/models/roles.model';
import { SearchInput } from '../../../shared/search-input/search-input';
import { SelectOption, SelectComponent } from '../../../shared/select/select';
import { UiButtonComponent } from '../../../shared/ui-button/ui-button';
import { CardListComponent } from '../../../shared/card-list/card-list';
import { MobileService } from '../../../core/services/mobile.service';
import { DatePipe } from '@angular/common';
import { LoadingComponent } from '../../../shared/loading/loading';
import dayjs from 'dayjs';

@Component({
  selector: 'app-usuarios',
  standalone: true,
  imports: [
    PageHeaderComponent,
    SummaryCardComponent,
    DataTablePaginado,
    FormsModule,
    ModalShellComponent,
    UsuariosFormComponent,
    ConfirmationModalComponent,
    SearchInput,
    SelectComponent,
    UiButtonComponent,
    CardListComponent,
    DatePipe,
    LoadingComponent,
  ],
  templateUrl: './usuarios.html',
  styleUrl: './usuarios.css',
})
export class UsuariosComponent {
  private readonly usuarioService = inject(UsuarioService);
  private readonly empresaService = inject(EmpresaService);
  private readonly rolesService = inject(RolesService);
  private readonly apiService = inject(ApiService);
  protected readonly mobileService = inject(MobileService);

  protected infoUsuarios: UsuariosInfoModel | null = null;
  protected data: UsuariosDataModel[] = [];
  protected loadingCard = false;
  protected loadingPage = true;
  private initialLoadsPending = 4;
  protected loadingTable = false;
  protected page = 1;
  protected pageSize = 10;
  protected totalItems = 0;
  protected searchInput = '';
  protected search = '';
  protected statusFilter = 'Activo';
  protected readonly statusOptions: SelectOption[] = [
    { label: 'Todos', value: 'Todos' },
    { label: 'Activos', value: 'Activo' },
    { label: 'Inactivos', value: 'Inactivo' },
  ];
  protected sortBy: string | undefined;
  protected sortDirection: 'asc' | 'desc' | undefined;
  protected empresas: EmpresasDataModel[] = [];
  protected roles: RolesDto[] = [];
  protected usuarioEditar: UsuarioFormValue | null = null;
  protected showForm = false;
  protected saveError = '';
  protected usuarioToDelete: UsuariosDataModel | null = null;
  protected showDeleteConfirmation = false;
  protected deletingUsuario = false;
  protected deleteStatus: ConfirmationStatus = 'active';
  protected deleteMessage = '';
  protected colorIcon = 'var(--red)';
  protected backgroundIcon = 'rgba(255, 0, 0, 0.1)';

  protected readonly columns: TableColumn<UsuariosDataModel>[] = [
    { key: 'nombre', label: 'Nombre', sortable: true },
    { key: 'usuario', label: 'Usuario' },
    {
      key: 'empresa',
      label: 'Empresa',
      sortable: true,
      formatter: (value: string) => value || 'Sin empresa',
    },
    {
      key: 'rolesUsuario',
      label: 'Roles',
      formatter: (_, row) => row.rolesUsuario.map((rol) => rol.rolCodigo).join(', ') || 'Sin rol',
    },
    {
      key: 'fechaCreacion',
      label: 'Fecha de registro',
      type: 'date',
      align: 'left',
      formatter: (value: string) => {
        return dayjs(value).format('YYYY-MM-DD HH:mm:ss');
      },
    },
    {
      key: 'estado',
      label: 'Estado',
      type: 'badge',
      sortable: true,
      align: 'center',
    },
    {
      key: 'estadoEmpresa',
      label: 'Estado empresa',
      type: 'badge',
      sortable: true,
      align: 'center',
      formatter: (value: string | null) => value ?? 'Sin empresa',
    },
    {
      key: '__actions',
      label: 'Acciones',
      type: 'actions',
      align: 'center',
      width: '120px',
      actions: [
        {
          id: 'edit',
          label: 'Editar',
          icon: 'fa-solid fa-pen',
          ariaLabel: 'Editar usuario',
        },
        {
          id: 'delete',
          label: 'Eliminar',
          icon: 'fa-solid fa-trash',
          ariaLabel: 'Eliminar usuario',
          cssClass: 'delete-action',
        },
      ],
    },
  ];

  constructor() {
    this.loadCard();
    this.loadUsuarios();
    this.loadEmpresas();
    this.loadRoles();
  }

  protected loadCard(): void {
    this.loadingCard = true;
    this.usuarioService
      .getUsuariosInfo()
      .pipe(
        finalize(() => {
          this.loadingCard = false;
          this.finishInitialLoad();
        }),
      )
      .subscribe({
        next: (response) => (this.infoUsuarios = response.success ? response.data : null),
        error: () => (this.infoUsuarios = null),
      });
  }

  protected loadUsuarios(): void {
    this.loadingTable = true;
    const request: PaginationRequest = {
      page: this.page,
      pageSize: this.pageSize,
      search: this.search,
      status: this.statusFilter,
      sortBy: this.sortBy,
      sortDirection: this.sortDirection,
    };

    this.usuarioService
      .getUsuariosDataPaginada(request)
      .pipe(
        finalize(() => {
          this.loadingTable = false;
          this.finishInitialLoad();
        }),
      )
      .subscribe({
        next: (response) => {
          if (!response.success || !response.data) {
            this.data = [];
            this.totalItems = 0;
            return;
          }

          this.data = response.data.items;
          this.page = response.data.page;
          this.pageSize = response.data.pageSize;
          this.totalItems = response.data.totalItems;
        },
        error: () => {
          this.data = [];
          this.totalItems = 0;
        },
      });
  }

  protected onPageChange(event: TablePageEvent): void {
    this.page = event.page;
    this.pageSize = event.pageSize;
    this.loadUsuarios();
  }

  protected onPageSizeChange(event: TablePageEvent): void {
    this.page = 1;
    this.pageSize = event.pageSize;
    this.loadUsuarios();
  }

  protected onSort(event: TableSortEvent): void {
    this.page = 1;
    this.sortBy = event.column ?? undefined;
    this.sortDirection = event.direction ?? undefined;
    this.loadUsuarios();
  }

  protected onSearch(): void {
    this.search = this.searchInput.trim();
    this.page = 1;
    this.loadUsuarios();
  }

  protected onStatusChange(value: string | string[] | null): void {
    if (typeof value !== 'string') return;
    this.statusFilter = value;
    this.page = 1;
    this.loadUsuarios();
  }

  protected nuevaUsuario(): void {
    this.usuarioEditar = null;
    this.saveError = '';
    this.showForm = true;
  }

  protected onAction(event: TableActionEvent<UsuariosDataModel>): void {
    if (event.action === 'edit') this.editarUsuario(event.row);
    if (event.action === 'delete') this.eliminarUsuario(event.row);
  }

  protected getRolesLabel(usuario: UsuariosDataModel): string {
    return usuario.rolesUsuario.map((rol) => rol.rolCodigo).join(', ') || 'Sin rol';
  }

  protected editarUsuario(usuario: UsuariosDataModel): void {
    this.saveError = '';
    this.usuarioEditar = {
      idUsuario: usuario.usuarioId,
      nombre: usuario.nombre,
      usuario: usuario.usuario,
      empresaId: usuario.empresaId,
      rolIds: usuario.rolesUsuario.map((rol) => rol.rolId),
    };
    this.showForm = true;
  }

  protected onUsuarioSaved(value: UsuarioFormValue): void {
    const { idUsuario, ...request } = value;
    const operation = idUsuario
      ? this.usuarioService.update(idUsuario, request)
      : this.usuarioService.create(request);

    operation.subscribe({
      next: (response) => {
        if (!response.success || !response.data) {
          this.saveError = response.message ?? 'No se pudo guardar el usuario. Intenta nuevamente.';
          return;
        }
        this.closeForm();
        this.loadCard();
        this.loadUsuarios();
      },
      error: (error: HttpErrorResponse) => {
        this.saveError = this.apiService.getMessage(
          error,
          'No se pudo guardar el usuario. Intenta nuevamente.',
        );
      },
    });
  }

  protected closeForm(): void {
    this.usuarioEditar = null;
    this.saveError = '';
    this.showForm = false;
  }

  protected eliminarUsuario(usuario: UsuariosDataModel): void {
    if (this.deletingUsuario) return;
    this.usuarioToDelete = usuario;
    this.deleteStatus = 'active';
    this.deleteMessage = `Se eliminará el usuario ${usuario.nombre}. Esta acción no se puede deshacer.`;
    this.showDeleteConfirmation = true;
  }

  protected confirmarEliminarUsuario(): void {
    if (!this.usuarioToDelete || this.deletingUsuario) return;
    this.deletingUsuario = true;
    this.usuarioService
      .desactivate(this.usuarioToDelete.usuarioId)
      .pipe(finalize(() => (this.deletingUsuario = false)))
      .subscribe({
        next: (response) => {
          if (!response.success || !response.data) {
            this.deleteStatus = 'error';
            this.deleteMessage = 'El usuario no pudo ser eliminado.';
            return;
          }
          this.deleteStatus = 'success';
          this.colorIcon = 'var(--green)';
          this.backgroundIcon = 'rgba(0, 128, 0, 0.1)';
          this.deleteMessage = 'El usuario fue eliminado correctamente.';
          this.loadCard();
          this.loadUsuarios();
        },
        error: (error: HttpErrorResponse) => {
          this.deleteStatus = 'error';
          this.deleteMessage = this.apiService.getMessage(
            error,
            'No se pudo eliminar el usuario. Intenta nuevamente.',
          );
        },
      });
  }

  protected closeDeleteConfirmation(): void {
    if (this.deletingUsuario) return;
    this.showDeleteConfirmation = false;
    this.usuarioToDelete = null;
    this.deleteStatus = 'active';
    this.deleteMessage = '';
    this.colorIcon = 'var(--red)';
    this.backgroundIcon = 'rgba(255, 0, 0, 0.1)';
  }

  private loadEmpresas(): void {
    this.empresaService
      .getAll()
      .pipe(finalize(() => this.finishInitialLoad()))
      .subscribe({
        next: (empresas) => (this.empresas = empresas),
        error: () => (this.empresas = []),
      });
  }

  private loadRoles(): void {
    this.rolesService
      .getAll()
      .pipe(finalize(() => this.finishInitialLoad()))
      .subscribe({
        next: (roles) => (this.roles = roles),
        error: () => (this.roles = []),
      });
  }

  private finishInitialLoad(): void {
    if (this.initialLoadsPending === 0) return;
    this.initialLoadsPending -= 1;
    if (this.initialLoadsPending === 0) this.loadingPage = false;
  }
}
